import {
  AfterViewInit,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { Router } from '@angular/router';
import { BookmarkRecord } from 'fl-bookmarks';
import { ContainerVm, ResourceVm } from 'qp-ui-interface';
import { ClevertapEvent } from 'src/app/analytics/clevertap/enum/clevertap.eventType';
import { ClevertapService } from 'src/app/analytics/clevertap/service/clevertap.service';
import { AppEventName } from 'src/app/analytics/newrelic/enums/EventName';
import { PageType } from 'src/app/analytics/newrelic/enums/PageType';
import { NewrelicService } from 'src/app/analytics/newrelic/services/newrelic.service';
import { NewrelicAppStore } from 'src/app/analytics/newrelic/store/newrelicApp.store';
import { DataService } from 'src/app/data.service';
import { LanguageService } from 'src/app/language.service';
import {
  IN_APP_BANNER_ID,
  isDirectPlayableTypes,
} from 'src/app/shared/common/const/common.const';
import { ContentType } from 'src/app/shared/enums/contentType';
import { LocalStorageKeys } from 'src/app/shared/LocalStorageKeys';
import { DialogService } from 'src/app/shared/services/dialog.service';
import { PlaybackService } from 'src/app/shared/services/playback.service';
import { RoutingService } from 'src/app/shared/services/routing.service';
import { FocusRestorationService } from 'src/app/shared/services/utility/focus-restoration.service';
import {
  FocusedCardData,
  ScrollMovement,
} from '../interfaces/cards.interfaces';
import { SimplePlayerService } from '../../simple-player/simple-player.service';

@Component({
  selector: 'app-storefront-horizontal-carousel',
  templateUrl: './storefront-horizontal-carousel.component.html',
  styleUrls: ['./storefront-horizontal-carousel.component.scss'],
})
export class StorefrontHorizontalCarouselComponent
  implements OnInit, AfterViewInit, OnDestroy
{
  @Input() containers: ContainerVm;
  @Input() aspectRatio: string = '2x3';
  @Input() showHeader: boolean = true;
  @Input() isTitleVisible: boolean = false;
  @Input() customId: string = '';
  @Input() verticalIndex: number = 0;
  @Input() index: number = 0;
  @Input() bookmark: BookmarkRecord | null = null;
  lang = 'en';
  @Input() continueWatchingResetId: string | null = null;
  @Input() inAppBanner: boolean = false;
  @Input() viewAllIndex: number = 20;
  @Input() canFocus: boolean = false;
  @Output() focusProcessed: EventEmitter<void> = new EventEmitter<void>();

  @Output() onCardClick: EventEmitter<boolean | undefined> = new EventEmitter<
    boolean | undefined
  >();
  @Output() focusLeft: EventEmitter<void> = new EventEmitter<void>();

  horizontalList: ResourceVm[] = [];
  updateRes: FocusedCardData | null = null;
  firstCardWidth: number = 0;

  scrollMovement: ScrollMovement = { right: '' };
  private actualMovement = 0;
  private _lastContinueWatchingResetId: string | null = null;
  private clickDebounceTimer: any = null;
  private isClickDisabled: boolean = false;
  private readonly CLICK_DEBOUNCE_DELAY: number = 800; // 800ms debounce for smart TV compatibility

  private keyPressDebounceTimer: any = null;
  private readonly KEY_PRESS_DEBOUNCE_DELAY: number = 100; // 100ms debounce for key presses

  constructor(
    private router: Router,
    private dataService: DataService,
    private readonly playbackService: PlaybackService,
    private readonly routingService: RoutingService,
    private readonly newrelic: NewrelicService,
    private readonly newrelicStore: NewrelicAppStore,
    private readonly languageService: LanguageService,
    private readonly clevertapService: ClevertapService,
    private readonly focusRestorationService: FocusRestorationService,
    private readonly dialogService: DialogService,
    private readonly simplePlayerService: SimplePlayerService
  ) {}

  ngOnInit(): void {
    this.lang = this.languageService.currLang;
    if (this.containers.viewAll && this.containers.viewMoreUrl?.length) {
      this.horizontalList = this.containers.resources.slice(
        0,
        this.viewAllIndex
      );
      let viewAll: ResourceVm = {
        id: this.containers.id,
        key: 'view-all',
        name: 'View All',
        type: 'view-all',
        contentType: 'view-all',
        image: '/assets/icons/placeholder_no_image.svg',
        trailers: [],
        keywords: [],
        collectionID: this.containers.id,
        collectionName: this.containers.name,
      };

      this.horizontalList.push(viewAll);
    } else {
      this.horizontalList = this.containers.resources;
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['containers']?.currentValue) {
      this.horizontalList = changes['containers'].currentValue.resources;
    }
    if (
      changes['continueWatchingResetId'] &&
      // changes['continueWatchingResetId'].currentValue !==
      //   this._lastContinueWatchingResetId &&
      this.containers.sourceType === 'continue_watching'
    ) {
      // this._lastContinueWatchingResetId =
      //   changes['continueWatchingResetId'].currentValue;
      this.horizontalList = this.containers.resources;
      this.resetCardTransforms();
    }
  }

  onImageError(event: Event): void {
    const fallback = '/assets/icons/placeholder_no_image.svg'; // or any SVG path you prefer
    const img = event.target as HTMLImageElement;

    if (img && img.src !== fallback) {
      img.src = fallback;
    }
  }

  private resetCardTransforms() {
    this.scrollMovement.right = '';
    this.actualMovement = 0;
    const name = `.horizontal-carousel-cards-${this.index}`;
    const horizontalCarousel = document.querySelector(name);
    horizontalCarousel?.setAttribute('data-index', '0');
  }

  ngAfterViewInit(): void {}

  onFocusCard(event: FocusedCardData) {
    this.updateRes = {
      resource:
        event.resource?.type === 'view-all'
          ? this.horizontalList[event.index - 1]
          : event.resource,
    };
    this.setCardIndex(event.verticalIndex, event.index);
    if (this.canFocus) {
      this.updateResource(this.updateRes);
      this.updateRes = null;
      this.focusProcessed.emit();
    }
    if (this.dataService.forcePlayVideo) {
      this.dialogService.closeAll();
      this.dataService.forcePlayVideo = false;
      const playableResource = this.horizontalList.find(
        (it) => it.id === this.dataService.selectedCardData.id
      );
      this.onClick(playableResource);
    }
  }

  onKeyUp() {
    if (this.updateRes) {
      // CRITICAL: Clear existing debounce timer to prevent multiple executions
      if (this.keyPressDebounceTimer) {
        clearTimeout(this.keyPressDebounceTimer);
      }

      // CRITICAL: Debounce key press handling for Smart TV remote controls
      this.keyPressDebounceTimer = setTimeout(() => {
        this.updateResource(this.updateRes);
        this.updateRes = null;
        this.keyPressDebounceTimer = null;
      }, this.KEY_PRESS_DEBOUNCE_DELAY);
    }
  }

  private updateResource(event: FocusedCardData) {
    if (!event) {
      return;
    }
    if (event.resource) {
      this.dataService.selectedCardData = event.resource;
    }
  }

  private setCardIndex(verticalIndex: number, horizontalIndex: number) {
    this.dataService.verticalIndex = verticalIndex;
    this.dataService.horizontalIndex = horizontalIndex;
  }

  onArrowLeft() {
    if (this.actualMovement === 0) {
      this.focusLeft.emit();
    } else if (this.actualMovement > 0) {
      this.actualMovement -= 1;
      this.updateCarouselTransform();
    }
  }

  onArrowRight() {
    if (this.actualMovement < this.horizontalList.length - 1) {
      this.actualMovement += 1;
      this.updateCarouselTransform();
    }
  }

  private updateCarouselTransform(): void {
    if (this.firstCardWidth) {
      this.scrollMovement.right = `${
        this.firstCardWidth * this.actualMovement
      }px`;
      return;
    }
    const track = document.getElementsByClassName(
      'horizontal-carousel-cards-' + this.index
    )[0];
    if (track) {
      const firstCard = track.querySelector('.app-cards-0') as HTMLElement;
      if (firstCard) {
        const cardWidth = this.getStyleValue(firstCard, 'width');
        const cardMargin =
          this.getStyleValue(firstCard, 'margin-left') +
          this.getStyleValue(firstCard, 'margin-right');

        /* this.scrollMovement.right = `${
          (cardWidth + cardMargin) * this.actualMovement
        }px`; */
        this.firstCardWidth = cardWidth + cardMargin;
        this.scrollMovement.right = `${
          this.firstCardWidth * this.actualMovement
        }px`;
      }
    }
  }

  private getStyleValue(element: HTMLElement, style: string): number {
    return +getComputedStyle(element).getPropertyValue(style).split('px')[0];
  }

  resetInitialPosition(): void {
    this.scrollMovement.right = '';
    this.actualMovement = 0;
  }

  onClick(resource: ResourceVm) {
    // Debouncer - prevent rapid successive clicks
    if (this.isClickDisabled || this.dialogService.hasOpenDialog()) {
      return; // Ignore rapid successive clicks
    }

    // Disable further clicks immediately
    this.isClickDisabled = true;

    // Set timer to re-enable clicks after debounce delay
    this.clickDebounceTimer = setTimeout(() => {
      this.isClickDisabled = false;
      this.clearClickDebounceTimer();
    }, this.CLICK_DEBOUNCE_DELAY);

    this.onClickAction(resource);
  }

  private onClickAction(resource: ResourceVm) {
    localStorage.removeItem(LocalStorageKeys.TAB_CHANGED);
    const { type, id: contentID } = resource;

    if (isDirectPlayableTypes.includes(resource.contentType as ContentType)) {
      this.router.navigate([`player/${type}/${contentID}`], {
        state: {
          type,
          id: contentID,
          analyticsResource: resource,
        },
      });
    } else if (resource.type === 'view-all') {
      this.dataService.selectedCardData = null;
      this.simplePlayerService.resetEvent();
      this.focusRestorationService.resetIndex();
      const viewAllContainer = Object.assign({}, this.containers);
      this.router.navigate([`/view-all/${resource.id}`], {
        state: {
          viewMoreUrl: this.containers.viewMoreUrl,
          viewMoreContainer: viewAllContainer,
        },
      });
      this.newrelic.sendAppEvent(AppEventName.VIEW_COLLECTION_DETAILS, {
        ...this.newrelicStore.getCollectionInfo(resource),
      });
      this.clevertapService.addEvent(ClevertapEvent.SEE_ALL_CLICKED, {
        trayName: this.containers.name[this.languageService.currLang],
        trayType: this.containers.containerType,
        source: 'TV',
      });
      // TODO: remove this after collection page is checked in tv app, also remove entire setCollectionSelectedCardData() function
      // this.setCollectionSelectedCardData();
      return;
    } else if (resource.type === 'collection') {
      this.focusRestorationService.resetIndex();
      if (resource.collectionType === 'storefront') {
        this.newrelic.sendAppEvent(AppEventName.VIEW_COLLECTION_DETAILS, {
          ...this.newrelicStore.getCollectionInfo(resource),
        });
        this.router.navigateByUrl(`collection-storefront/${resource.id}`);
        return;
      }
      const isGenre = resource.containerName?.en
        ?.toLowerCase()
        ?.includes('genre');
      const container = Object.assign({}, this.containers);
      container.name = resource.title;
      container.id = resource.id;
      if (!resource.contentUrl) {
        return;
      }
      this.newrelic.sendAppEvent(AppEventName.VIEW_COLLECTION_DETAILS, {
        ...this.newrelicStore.getCollectionInfo(resource),
      });
      this.clevertapService.addEvent(ClevertapEvent.SEE_ALL_CLICKED, {
        trayName: this.containers.name[this.languageService.currLang],
        trayType: this.containers.containerType,
        source: 'TV',
      });
      this.router.navigateByUrl(
        `/${isGenre ? 'genres' : 'artist'}/${resource.id}`,
        {
          state: {
            collectionId: resource.id,
            viewMoreUrl: resource.collectionUrl,
            container,
          },
        }
      );
    } else {
      if (this.containers.sourceType === 'continue_watching') {
        const result = this.playbackService.showPopup(
          resource,
          undefined,
          PageType.Browse
        );
        if (!result) {
          this.routingService.navigateToPlayerWithValidation({
            resource,
            navigationPath: `player/${resource.contentType}/${resource.id}`,
            state: {
              isDirectPlayer: true,
              analyticsResource: resource,
            },
          });
        }
      } else {
        this.dataService.detailResource = null;
        this.router.navigate([`/${resource.contentType}/${resource.uniqueId}`]);
      }
      this.dataService.currentResourceContainerName =
        (resource.containerName &&
          resource.containerName[this.languageService.currLang]) ||
        undefined;
      this.dataService.currentResourceContainerId =
        resource.containerId || undefined;
      this.dataService.currentResourcePositionInTray =
        resource.positionInTray !== undefined &&
        resource.positionInTray !== null
          ? resource.positionInTray
          : undefined;
    }
  }

  get localizedTitle(): string {
    if (this.containers.id === IN_APP_BANNER_ID) {
      return this.containers.name?.[this.lang];
    } else if (this.containers?.localizedName?.[this.lang]?.n) {
      return this.containers?.localizedName?.[this.lang]?.n;
    } else if (this.containers?.localizedName?.['en']?.n === 'Popular List') {
      return this.languageService.getTranslatedText('popular_list');
    } else if (
      this.containers?.localizedName?.['en']?.n === 'Continue Watching'
    ) {
      return this.languageService.getTranslatedText(
        'my_content.continue_watching'
      );
    } else if (this.containers?.localizedName?.['en']?.n === 'Watchlist') {
      return this.languageService.getTranslatedText('watchList');
    } else if (this.containers?.localizedName?.['en']?.n === 'Rentals') {
      return this.languageService.getTranslatedText('my_rentals');
    } else if (this.containers.name === 'recommended_for_you') {
      return this.languageService.getTranslatedText('recommended_for_you');
    } else {
      return this.containers?.localizedName?.['en']?.n || '';
    }
  }

  trackByIndex(index: number, item: any): any {
    return item && item.id ? item.id : index;
  }

  ngOnDestroy(): void {
    this.forceClean();
  }

  private clearClickDebounceTimer(): void {
    if (this.clickDebounceTimer) {
      clearTimeout(this.clickDebounceTimer);
      this.clickDebounceTimer = null;
    }
  }

  private resetClickState(): void {
    this.isClickDisabled = false;
    this.clearClickDebounceTimer();
  }

  private forceClean() {
    this.clearClickDebounceTimer();
    this.resetClickState();
    if (this.keyPressDebounceTimer) {
      clearTimeout(this.keyPressDebounceTimer);
      this.keyPressDebounceTimer = null;
    }
    this.containers = null;
    this.aspectRatio = null;
    this.showHeader = true;
    this.isTitleVisible = false;
    this.customId = null;
    this.verticalIndex = null;
    this.index = null;
    this.bookmark = null;
    this.lang = null;
    this.continueWatchingResetId = null;
    this.inAppBanner = false;
    this.viewAllIndex = 20;
    this.horizontalList = [];
    this.updateRes = null;
    this.firstCardWidth = 0;
    this.scrollMovement = { right: '' };
    this.actualMovement = 0;
    this._lastContinueWatchingResetId = null;
    this.onCardClick.complete();
    this.focusLeft.complete();
    this.focusProcessed.complete();
  }
}
